var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { Input } from '../Input';
import moment from 'moment';
import InputMask from 'react-input-mask';
import classnames from 'classnames';
import { keys } from '../../utilities/keyCodes';
import { expandValueAtIndex, findMaskFieldBoundariesAtPosition, getValueAtIndex, setValueAtIndex, } from '../../utilities/maskUtils';
const round = (year, to = 100) => year - (year % to);
export const InputDateMask = (_a) => {
    var { alwaysShowMask, className, dateFormat = 'MM/DD/YYYY', disabled, error, maskChar = '_', maxDate, minDate, onChange, placeholder, shortLabel, size, value, tabIndex, inputRef } = _a, props = __rest(_a, ["alwaysShowMask", "className", "dateFormat", "disabled", "error", "maskChar", "maxDate", "minDate", "onChange", "placeholder", "shortLabel", "size", "value", "tabIndex", "inputRef"]);
    const [maskValue, setMask] = React.useState('');
    const [expandValueAtCursor, setExpandValueAtCursor] = React.useState(false);
    const [cursorPosition, setCursorPosition] = React.useState(null);
    let falseEmptyChange = false;
    const handleKeyDown = (evt) => {
        if (evt.key === keys.delete) {
            onChange(undefined);
            setMask('');
        }
        if (evt.key === keys.slash) {
            setExpandValueAtCursor(true);
        }
    };
    const expandMaskValue = (mask, cursorPosition) => {
        let value = mask;
        const parsedValue = parseInt(getValueAtIndex(mask, cursorPosition), 10);
        if (!parsedValue)
            return value;
        const [startIndex, endIndex] = findMaskFieldBoundariesAtPosition(mask, cursorPosition);
        const desiredMaskLength = endIndex - startIndex;
        if (desiredMaskLength === 4) {
            // End early if full year already exists
            if (parsedValue.toString().length === 4)
                return value;
            /**
             * auto fills year if value is between the beginning of century
             * and 5 years ahead
             *
             * @todo: Change year calculations to use current year for all non-existant values
             * E.g. 7 -> 2027, 20 -> 2020, 181 -> 2181, assuming current year is 2021
             */
            const currentYear = new Date().getFullYear();
            const beginningOfCentury = round(currentYear, 100);
            const yearsFromBeginningOfCentury = currentYear - beginningOfCentury + 5;
            let year = parsedValue + beginningOfCentury;
            if (parsedValue > yearsFromBeginningOfCentury) {
                year -= parsedValue >= 100 ? 1000 : 100;
            }
            value = setValueAtIndex(mask, cursorPosition, year);
        }
        else {
            value = expandValueAtIndex(mask, cursorPosition);
        }
        return value;
    };
    const updateMaskValue = (value) => {
        setMask(value);
        if (isMaskValid(value)) {
            onChange(parseDate(value));
        }
        else {
            onChange(undefined);
        }
    };
    const handleBeforeMaskedValueChange = (newState, oldState) => {
        var _a, _b, _c;
        falseEmptyChange =
            !/\d/.test(newState.value) && !/\d/.test(oldState.value);
        if (((_a = newState === null || newState === void 0 ? void 0 : newState.selection) === null || _a === void 0 ? void 0 : _a.start) === ((_b = newState === null || newState === void 0 ? void 0 : newState.selection) === null || _b === void 0 ? void 0 : _b.end)) {
            setCursorPosition((_c = newState === null || newState === void 0 ? void 0 : newState.selection) === null || _c === void 0 ? void 0 : _c.start);
        }
        if (expandValueAtCursor) {
            setExpandValueAtCursor(false);
            const value = expandMaskValue(newState.value, newState.selection.start);
            if (value === maskValue) {
                return newState;
            }
            updateMaskValue(value);
            // reposition cursor
            const [, endIndex] = findMaskFieldBoundariesAtPosition(newState.value, newState.selection.start);
            newState.selection = { start: endIndex, end: endIndex };
            newState.value = value;
        }
        return newState;
    };
    const handleBlur = () => {
        var _a;
        if (cursorPosition) {
            const expandedValue = (_a = expandMaskValue(maskValue, cursorPosition)) !== null && _a !== void 0 ? _a : maskValue;
            if (expandedValue !== maskValue) {
                updateMaskValue(expandMaskValue(maskValue, cursorPosition));
                setCursorPosition(null);
            }
        }
    };
    const handleChange = (event) => {
        const value = event.target.value;
        setMask(value);
        if (falseEmptyChange) {
            return;
        }
        if (isMaskValid(value)) {
            onChange(parseDate(value));
        }
        else {
            onChange(undefined);
        }
    };
    const getMaskError = () => {
        return (value === undefined && doesMaskExist() && isMaskInvalid(maskValue));
    };
    const getValueError = () => {
        return value !== undefined && !isDateInRange(value);
    };
    const formatDate = (date) => {
        return date ? moment(date).format(dateFormat) : '';
    };
    const parseDate = React.useCallback((date) => moment(date, dateFormat).toDate(), [dateFormat]);
    const doesMaskExist = () => {
        return maskValue !== '' && maskValue !== undefined;
    };
    const isMaskInvalid = (mask) => {
        return !(mask === null || mask === void 0 ? void 0 : mask.includes(maskChar)) && !isDateStringValid(mask);
    };
    const isDateInRange = React.useCallback((date) => {
        if (minDate && maxDate) {
            return date >= minDate && date <= maxDate;
        }
        else if (minDate) {
            return date >= minDate;
        }
        else if (maxDate) {
            return date <= maxDate;
        }
        return true;
    }, [minDate, maxDate]);
    const isDateStringValid = React.useCallback((date) => moment(date, dateFormat).isValid() &&
        isDateInRange(parseDate(date)), [dateFormat, isDateInRange, parseDate]);
    const isMaskValid = React.useCallback((mask) => !(mask === null || mask === void 0 ? void 0 : mask.includes(maskChar)) && isDateStringValid(mask), [maskChar, isDateStringValid]);
    React.useEffect(() => {
        if (value === undefined && isMaskValid(maskValue)) {
            setMask('');
        }
    }, [value, maskValue, isMaskValid]);
    /*
     * This replaces the date format with a random number (9)
     * This gets turned back to `maskChar` a few lines below.
     * I don't know why we're doing this.
     */
    const mask = dateFormat.replace(/[MmDdYyo]/g, '9');
    const realError = error || getMaskError() || getValueError();
    const realPlaceholder = placeholder || mask.replace(/9/g, maskChar);
    const classes = classnames('InputDateMask', className, {
        'InputDateMask--error': realError,
    });
    return (React.createElement(InputMask, Object.assign({}, props, { mask: mask, disabled: disabled, maskChar: maskChar, onChange: handleChange, onKeyDown: handleKeyDown, onBlur: handleBlur, beforeMaskedValueChange: handleBeforeMaskedValueChange, alwaysShowMask: alwaysShowMask, value: formatDate(value) || maskValue }), (props) => (React.createElement(Input, Object.assign({}, props, { inputRef: inputRef, shortLabel: shortLabel, className: classes, size: size, fluid: true, error: realError, disabled: disabled, placeholder: realPlaceholder, tabIndex: tabIndex })))));
};
//# sourceMappingURL=InputDateMask.js.map